import { ReactNode, useEffect, useState } from 'react';

import moment from 'moment';

interface CountdownProps {
  datetime: number;
  label?: string;
}

interface StatProps {
  value: string;
  label: string;
}

interface FooterLinkProps {
  text: string;
  href: string;
}

interface ShareProps {
  enabled: boolean;
  heading?: string;
}

interface HeroProps {
  backgroundImage?: string;
  headline?: string;
  subheadline?: string;
  listItems?: string[];
  stats?: StatProps[];
  ctaText?: string;
  ctaLink?: string;
  countdown?: CountdownProps;
  footerText?: string;
  footerLink?: FooterLinkProps;
  share?: ShareProps;
  noFooter?: boolean;
  children?: ReactNode;
}

function CountdownTimer({ datetime, label }: CountdownProps) {
  const [time, setTime] = useState({
    days: 0,
    hours: '00',
    minutes: '00',
    seconds: '00',
  });

  useEffect(() => {
    const timer = setInterval(() => {
      const startTime = moment();
      const endTime = datetime ? moment.unix(datetime) : moment();
      const daysDiff = endTime.diff(startTime, 'days');
      const hrsDiff = moment.utc(endTime.diff(startTime)).format('HH');
      const minDiff = moment.utc(endTime.diff(startTime)).format('mm');
      const secDiff = moment.utc(endTime.diff(startTime)).format('ss');

      setTime({
        days: daysDiff,
        hours: hrsDiff,
        minutes: minDiff,
        seconds: secDiff,
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [datetime]);

  return (
    <div className="au-w-full">
      <div className="au-text-center au-mb-6">
        <div className="au-text-lg au-font-medium au-text-gray-800">
          {label || 'Countdown to Registration'}
        </div>
      </div>
      <div className="au-grid au-grid-cols-4 au-gap-4 au-w-full">
        {[
          { value: time.days, label: 'Days' },
          { value: time.hours, label: 'Hours' },
          { value: time.minutes, label: 'Minutes' },
          { value: time.seconds, label: 'Seconds' },
        ].map(item => (
          <div key={item.label} className="au-text-center">
            <div className="au-text-2xl au-font-bold">{item.value}</div>
            <div className="au-text-sm">{item.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Hero({
  backgroundImage,
  headline,
  subheadline,
  listItems = [],
  stats = [],
  ctaText,
  ctaLink,
  countdown,
  footerText,
  footerLink,
  share,
  noFooter = false,
  children,
}: HeroProps) {
  return (
    <div className="au-relative au-flex au-flex-col au-min-h-screen">
      <div className="au-flex-grow">
        {backgroundImage && (
          <div className="au-absolute au-inset-0 au-z-0">
            <div className="au-absolute au-inset-0 au-bg-gradient-to-r au-from-black/40 au-to-black/20 au-z-10" />
            <img
              src={backgroundImage}
              alt="Background"
              className="au-absolute au-inset-0 au-w-full au-h-full au-object-cover"
            />
          </div>
        )}

        <div className="au-relative au-z-10 au-px-4 au-py-12">
          <div className="au-container au-mx-auto">
            <div className="au-mb-12 au-max-w-2xl">
              {subheadline && (
                <div className="au-text-white au-text-sm au-mb-6 au-uppercase au-tracking-wider">
                  {subheadline}
                </div>
              )}

              {headline && (
                <h1 className="au-text-white au-text-4xl md:au-text-5xl au-font-bold au-leading-tight au-mb-8">
                  {headline}
                </h1>
              )}

              {listItems?.length > 0 && (
                <ul className="au-list-none au-pl-0 au-text-white au-space-y-2 au-mb-8">
                  {listItems.map(item => (
                    <li key={item} className="au-text-base">
                      {item}
                    </li>
                  ))}
                </ul>
              )}

              {ctaText && ctaLink && (
                <a
                  href={ctaLink}
                  className="au-inline-block au-bg-blue-600 au-text-white au-py-3 au-text-base au-font-medium au-mt-6"
                >
                  {ctaText}
                </a>
              )}
              {children}
            </div>

            {stats?.length > 0 && (
              <div className="au-grid au-grid-cols-2 md:au-grid-cols-4 au-gap-8 au-mb-12">
                {stats.map(stat => (
                  <div
                    key={stat.label}
                    className="au-text-center au-text-white"
                  >
                    <div className="au-text-4xl md:au-text-5xl au-font-bold au-mb-1">
                      {stat.value}
                    </div>
                    <div className="au-text-sm">{stat.label}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {!noFooter && (
        <div className="au-relative au-z-10 au-mt-auto">
          {countdown && (
            <div className="au-bg-gray-200 au-py-6 au-px-4">
              <div className="au-container au-mx-auto">
                <div className="au-max-w-lg au-mx-auto">
                  <CountdownTimer {...countdown} />
                </div>
              </div>
            </div>
          )}

          {(footerText || footerLink) && (
            <div className="au-bg-gray-800 au-text-white au-py-8 au-px-4">
              <div className="au-container au-mx-auto">
                {footerText && (
                  <div className="au-text-xl au-mb-4">{footerText}</div>
                )}

                {footerLink && (
                  <div>
                    <a
                      href={footerLink.href}
                      className="au-text-white au-flex au-items-center"
                    >
                      {footerLink.text}
                      <svg
                        className="au-ml-2 au-w-5 au-h-5"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M9 5l7 7-7 7"
                          stroke="currentColor"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </a>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

Hero.attributes = {
  headline: {
    type: 'string',
  },
  subheadline: {
    type: 'string',
  },
  backgroundImage: {
    type: 'string',
  },
  ctaText: {
    type: 'string',
  },
  ctaLink: {
    type: 'string',
  },
  listItems: {
    type: 'array',
  },
  stats: {
    type: 'array',
  },
  countdown: {
    type: 'object',
  },
  footerText: {
    type: 'string',
  },
  footerLink: {
    type: 'object',
  },
  share: {
    type: 'object',
  },
  noFooter: {
    type: 'boolean',
  },
};

export default Hero;
export type {
  HeroProps,
  CountdownProps,
  StatProps,
  FooterLinkProps,
  ShareProps,
};
